import { z } from 'zod';

import { limitPolicySchema, limitStatusSchema } from './limits';
import { remoteCommandKindSchema } from './agent';
import { sessionEndReasonSchema } from './session';

/**
 * Contract between `arena-api` and the Arena section of the back-office
 * (`apps/admin-web`). Routes live under `/api/arena`.
 */

const instant = z.string().datetime({ offset: true });
const uuid = z.string().uuid();

export const pinSchema = z.string().regex(/^\d{4}$/, 'O PIN tem 4 dígitos.');

export const arenaSchema = z.object({ id: uuid, name: z.string(), slug: z.string() });
export type Arena = z.infer<typeof arenaSchema>;

export const createArenaSchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{2,40}$/),
});

/**
 * What the public site (`growup.gg/arena`) shows about an arena, filled in the
 * back-office (Arena > Configuração). Only `published` arenas leave arena-api,
 * through `GET /internal/arenas/published`, which site-api reads.
 */
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora no formato HH:MM.');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();

/** One opening slot; a day may have two (lunch break). 1 = Monday ... 7 = Sunday. */
export const openingSlotSchema = z
  .object({ weekday: z.number().int().min(1).max(7), opens: time, closes: time })
  .refine((s) => s.opens < s.closes, 'A hora de fecho tem de ser depois da de abertura.');
export type OpeningSlot = z.infer<typeof openingSlotSchema>;

export const arenaDetailsSchema = z.object({
  description: optionalText(600),
  address: optionalText(200),
  postalCode: optionalText(20),
  city: optionalText(80),
  mapUrl: optionalText(500).refine(
    (v) => v === null || /^https:\/\//.test(v),
    'O link do mapa tem de começar por https://.',
  ),
  phone: optionalText(30),
  email: optionalText(120).refine(
    (v) => v === null || z.string().email().safeParse(v).success,
    'Email inválido.',
  ),
  openingHours: z.array(openingSlotSchema).max(21),
  hoursNote: optionalText(200),
  published: z.boolean(),
});
export type ArenaDetails = z.infer<typeof arenaDetailsSchema>;

export const EMPTY_ARENA_DETAILS: ArenaDetails = {
  description: null,
  address: null,
  postalCode: null,
  city: null,
  mapUrl: null,
  phone: null,
  email: null,
  openingHours: [],
  hoursNote: null,
  published: false,
};

/** One entry of `GET /internal/arenas/published`. */
export const publishedArenaSchema = arenaDetailsSchema
  .omit({ published: true })
  .extend({ name: z.string(), slug: z.string() });
export type PublishedArena = z.infer<typeof publishedArenaSchema>;

export const createWorkstationSchema = z.object({
  name: z.string().trim().min(1).max(40),
  posX: z.number().int().min(0).max(100).default(0),
  posY: z.number().int().min(0).max(100).default(0),
});

export const updateWorkstationSchema = z.object({
  name: z.string().trim().min(1).max(40).optional(),
  posX: z.number().int().min(0).max(100).optional(),
  posY: z.number().int().min(0).max(100).optional(),
});

/** Returned once when a workstation is created or its token rotated. Never stored in clear. */
export const workstationTokenSchema = z.object({ workstationId: uuid, token: z.string() });

export const roomSessionSchema = z.object({
  id: uuid,
  youthAccountId: uuid.nullable(),
  /** Only the login: monitors never see a youth's personal data (RN-A16). */
  login: z.string().nullable(),
  isEvent: z.boolean(),
  startedAt: instant,
  lastSeenAt: instant,
  duringBreak: z.boolean(),
  limits: limitStatusSchema.nullable(),
});
export type RoomSession = z.infer<typeof roomSessionSchema>;

export const roomWorkstationSchema = z.object({
  id: uuid,
  name: z.string(),
  posX: z.number(),
  posY: z.number(),
  online: z.boolean(),
  lastSeenAt: instant.nullable(),
  session: roomSessionSchema.nullable(),
  pendingCommand: z
    .object({ id: uuid, kind: remoteCommandKindSchema, executeAt: instant })
    .nullable(),
});
export type RoomWorkstation = z.infer<typeof roomWorkstationSchema>;

export const waitlistEntrySchema = z.object({
  id: uuid,
  youthAccountId: uuid,
  login: z.string(),
  addedAt: instant,
});
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;

export const roomResponseSchema = z.object({
  arena: arenaSchema,
  policy: limitPolicySchema,
  workstations: z.array(roomWorkstationSchema),
  waitlist: z.array(waitlistEntrySchema),
  serverTime: instant,
});
export type RoomResponse = z.infer<typeof roomResponseSchema>;

/**
 * Why a youth can or cannot unlock a PC, as members-api answers it
 * (`arena-eligibility`), plus `deactivated` from the Arena itself and
 * `unavailable` when members-api did not answer.
 */
export const unlockStatusSchema = z.enum([
  'ok',
  'not_member',
  'no_profile',
  'no_guardian_authorization',
  'deactivated',
  'unavailable',
]);
export type UnlockStatus = z.infer<typeof unlockStatusSchema>;

export const youthSummarySchema = z.object({
  accountId: uuid,
  login: z.string(),
  canUnlock: z.boolean(),
  unlockStatus: unlockStatusSchema,
  isAssociate: z.boolean(),
  meritUntil: z.string().nullable(),
  limits: limitStatusSchema,
});
export type YouthSummary = z.infer<typeof youthSummarySchema>;

/**
 * Registration in the room (RF-A2), second step. The back-office first calls
 * members-api (`POST /api/members/youths`), which creates the account and
 * keeps the encrypted personal data; then it sends here the account id, the
 * login and the PIN, which only ever live in the `arena` database.
 */
export const registerYouthSchema = z.object({
  accountId: uuid,
  login: z.string().trim().min(3).max(32),
  pin: pinSchema,
});
export type RegisterYouth = z.infer<typeof registerYouthSchema>;

export const registerYouthResponseSchema = z.object({
  accountId: uuid,
  login: z.string(),
});

export const setPinSchema = z.object({ pin: pinSchema });
export const setMeritSchema = z.object({ validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) });
export const grantTimeSchema = z.object({
  arenaId: uuid,
  minutes: z.number().int().min(5).max(180),
});
export const addToWaitlistSchema = z.object({ youthAccountId: uuid });
export const lockWorkstationSchema = z.object({ message: z.string().trim().max(200).optional() });
export const closeSessionSchema = z.object({
  endedAt: instant.optional(),
  note: z.string().trim().min(3).max(300),
});

export const sessionRowSchema = z.object({
  id: uuid,
  arenaId: uuid,
  workstationName: z.string(),
  youthAccountId: uuid.nullable(),
  login: z.string().nullable(),
  isEvent: z.boolean(),
  startedAt: instant,
  endedAt: instant.nullable(),
  endReason: sessionEndReasonSchema.nullable(),
  duringBreak: z.boolean(),
  minutes: z.number(),
});
export type SessionRow = z.infer<typeof sessionRowSchema>;

export const updatePolicySchema = limitPolicySchema;
